import type { ZrmClickstreamListing } from '@zg-rentals/clickstream';
import { getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';
import {
  SEMANTIC_EVENT_NAME,
  TOPIC_TAG_TXT,
  TRIGGER_LOCATION,
  TRIGGER_OBJECT,
  TRIGGER_SOURCE,
  TRIGGER_TYPE,
} from './globals';
import { trackBaseEvent, trackListingEvent, trackUserEvent } from '../../analytics';
import type { ZrmClickstreamUserOrNull } from '../../analytics/types';

export type TourActionTriggerSource = 'decline_tour' | 'confirm_tour' | 'cancel_tour' | 'reschedule_tour';

// https://dataportal.zillowgroup.net/event-registry/7123
// Lead Management: Tour related actions in renter details TourStatusCard (ie: cancel, confirm, decline)
export const emitLeadMgmtTourActionEvent = (triggerSource: TourActionTriggerSource) =>
  trackBaseEvent({
    envelope: {
      event_type_id: '7123',
      event_type_version_id: '1',
      event_template_id: '171',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: TRIGGER_LOCATION.LeadProfileCard,
      trigger_source_nm: triggerSource,
      trigger_object_nm: TRIGGER_OBJECT.NoTriggerObject,
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.TourSchedulingRenterDetails,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmTourScheduling],
    },
  });

// https://dataportal.zillowgroup.net/event-registry/7124
// Inbox: Tour related actions in TourStatusCard (ie: cancel, confirm, decline)
export const INBOX_TOUR_ACTION_EVENT_TYPE_ID = '7124';
export const emitInboxTourActionEvent = (
  user: ZrmClickstreamUserOrNull,
  triggerSource: TourActionTriggerSource,
  listing: ZrmClickstreamListing,
) =>
  trackListingEvent({
    envelope: {
      event_type_id: INBOX_TOUR_ACTION_EVENT_TYPE_ID,
      event_type_version_id: '2',
      event_template_id: '168',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: TRIGGER_LOCATION.IreneInbox,
      trigger_source_nm: triggerSource,
      trigger_object_nm: TRIGGER_OBJECT.NoTriggerObject,
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.TourSchedulingRRMPActions,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmTourScheduling],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/7177
export const LEAD_MGMT_TOUR_MODAL_ACTION_EVENT_TYPE_ID = '7177';
export const emitLeadMgmtTourModalActionEvent = (user: ZrmClickstreamUserOrNull, listing: ZrmClickstreamListing) =>
  trackListingEvent({
    envelope: {
      event_type_id: LEAD_MGMT_TOUR_MODAL_ACTION_EVENT_TYPE_ID,
      event_type_version_id: '1',
      event_template_id: '167',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: TRIGGER_LOCATION.DeclineTourModal,
      trigger_source_nm: TRIGGER_SOURCE.ConfirmDeclineTour,
      trigger_object_nm: TRIGGER_OBJECT.NoTriggerObject,
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.TourSchedulingLeadMgmtDeclineModal,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmTourScheduling],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/7180
export const INBOX_TOUR_MODAL_ACTION_EVENT_TYPE_ID = '7180';
export const emitInboxTourModalActionEvent = (user: ZrmClickstreamUserOrNull) =>
  trackUserEvent({
    envelope: {
      event_type_id: INBOX_TOUR_MODAL_ACTION_EVENT_TYPE_ID,
      event_type_version_id: '1',
      event_template_id: '167',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: TRIGGER_LOCATION.DeclineTourModal,
      trigger_source_nm: TRIGGER_SOURCE.ConfirmDeclineTour,
      trigger_object_nm: TRIGGER_OBJECT.NoTriggerObject,
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.TourSchedulingInboxDeclineModal,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmTourScheduling],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6636
export const LEAD_MNGMT_TOUR_UPSELL_BANNER_CLICK_EVENT_TYPE_ID = '6636';
export const emitLeadMgmtTourUpsellBannerClickEvent = (
  user: ZrmClickstreamUserOrNull,
  listing: ZrmClickstreamListing,
) =>
  trackListingEvent({
    envelope: {
      event_type_id: LEAD_MNGMT_TOUR_UPSELL_BANNER_CLICK_EVENT_TYPE_ID,
      event_type_version_id: '2',
      event_template_id: '168',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: TRIGGER_LOCATION.LeadProfileCard,
      trigger_source_nm: 'setup_tour_availability',
      trigger_object_nm: 'tour_module',
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.TourSchedulingEntryPoint,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmTourScheduling],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/7262
export const LEAD_MNGMT_TOUR_UPSELL_MODAL_CLICK_EVENT_TYPE_ID = '7262';
export const emitLeadMgmtTourUpsellModalClickEvent = (user: ZrmClickstreamUserOrNull) =>
  trackUserEvent({
    envelope: {
      event_type_id: LEAD_MNGMT_TOUR_UPSELL_MODAL_CLICK_EVENT_TYPE_ID,
      event_type_version_id: '1',
      event_template_id: '167',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: TRIGGER_LOCATION.LeadManagementPage,
      trigger_source_nm: 'add_availability',
      trigger_object_nm: TRIGGER_OBJECT.NoTriggerObject,
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.TourSchedulingLeadMgmtUpsell,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmTourScheduling],
    },
    ...getZrmUserInfo(user),
  });
